import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from 'recharts';
import { MoreHorizontal, RefreshCw } from 'lucide-react';
import { useDashboard } from '../context/DashboardContext';

const s = {
  grid: {
    display: 'grid',
    gridTemplateColumns: '1.6fr 1.2fr 1.2fr',
    gridTemplateRows: '1fr 1fr',
    gap: 14,
    flex: 1,
    minHeight: 0,
    position: 'relative',
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    padding: '16px 18px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
    transition: 'opacity 0.25s',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 14,
    flexShrink: 0,
  },
  cardTitle: { fontSize: 'clamp(9px, 0.9vw, 11px)', fontWeight: 700, color: '#2d3a4a' },
  moreBtn: {
    border: 'none', background: 'transparent', cursor: 'pointer',
    color: '#9ca3af', display: 'flex', alignItems: 'center', borderRadius: 6, padding: 4,
  },
  legend: {
    display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8, flex: 1,
  },
  legendItem: {
    display: 'flex', alignItems: 'center', gap: 6, fontSize: 'clamp(8px, 0.7vw, 9px)', color: '#4a5568', minWidth: 0,
  },
  legendLabel: {
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1,
  },
  legendDot: { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  pieWrapper: { display: 'flex', alignItems: 'center', gap: 8, flex: 1, minHeight: 0 },
  barItem: {
    display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, fontSize: 'clamp(8px, 0.7vw, 9px)',
  },
  barLabel: { width: 'clamp(100px, 17vw, 230px)', textAlign: 'left', color: '#6b7a8d', flexShrink: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  barTrack: { flex: 1, height: 8, background: '#edf2f7', borderRadius: 4, overflow: 'hidden', minWidth: 0, maxWidth: 80 },
  barFill: { height: '100%', borderRadius: 4, background: 'linear-gradient(90deg,#e05252,#9b2c2c)' },
  barValue: { width: 'clamp(60px, 5.5vw, 82px)', textAlign: 'right', fontWeight: 700, color: '#2d3a4a', fontSize: 'clamp(8px, 0.7vw, 9px)', flexShrink: 0, whiteSpace: 'nowrap' },
  // Loading overlay
  overlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,255,255,0.7)',
    borderRadius: 12,
    zIndex: 10,
    backdropFilter: 'blur(2px)',
  },
  spinnerWrap: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
  },
  spinnerText: { fontSize: 'clamp(10px, 1vw, 12px)', color: '#6b7a8d', fontWeight: 500 },
};

const RADIAN = Math.PI / 180;
const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={8} fontWeight={700}>
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
};

const tooltipStyle = {
  background: '#fff', border: '1px solid #e8eaed',
  borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', fontSize: 'clamp(10px, 1vw, 12px)',
};

const fmtNum = v => (v == null ? '-' : Number(v).toLocaleString('id-ID'));

function LoadingOverlay() {
  return (
    <div style={s.overlay}>
      <div style={s.spinnerWrap}>
        <RefreshCw size={22} color="#9b2c2c" className="spin" />
        <span style={s.spinnerText}>Memuat data…</span>
      </div>
    </div>
  );
}

function PieCard({ title, data }) {
  return (
    <div style={s.card}>
      <div style={s.cardHeader}>
        <span style={s.cardTitle}>{title}</span>
        <button style={s.moreBtn}><MoreHorizontal size={16} /></button>
      </div>
      <div style={s.pieWrapper}>
        <ResponsiveContainer width="50%" height="100%">
          <PieChart>
            <Pie data={data} cx="50%" cy="50%" outerRadius="80%" dataKey="value" labelLine={false} label={renderLabel}>
              {data.map((entry, i) => <Cell key={i} fill={entry.color} />)}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div style={{ ...s.legend, flex: 1 }}>
          {data.map((d, i) => (
            <div key={i} style={s.legendItem}>
              <div style={{ ...s.legendDot, background: d.color }} />
              <span style={s.legendLabel}>{d.name}</span>
              <span style={{ fontWeight: 700, flexShrink: 0 }}>{d.value}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TopListCard({ title, data }) {
  const maxVal = Math.max(...data.map(d => d.value), 1);
  return (
    <div style={s.card}>
      <div style={s.cardHeader}>
        <span style={s.cardTitle}>{title}</span>
        <button style={s.moreBtn}><MoreHorizontal size={16} /></button>
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        {data.map((d, i) => (
          <div key={i} style={s.barItem}>
            <span style={s.barLabel}>{d.name}</span>
            <div style={s.barTrack}>
              <div style={{ ...s.barFill, width: `${(d.value / maxVal) * 100}%`, transition: 'width 0.4s ease' }} />
            </div>
            <span style={s.barValue}>{fmtNum(d.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Charts() {
  const { data, loading } = useDashboard();

  const mtdChart      = data?.mtdChart      ?? [];
  const channelPie    = data?.channelPie    ?? [];
  const divisiPie     = data?.divisiPie     ?? [];
  const top10Products = data?.top10Products ?? [];
  const top10Outlets  = data?.top10Outlets  ?? [];

  return (
    <div style={{ ...s.grid, opacity: loading ? 0.6 : 1 }}>
      {loading && <LoadingOverlay />}

      {/* MTD Composed Chart */}
      <div style={{ ...s.card, gridColumn: '1', gridRow: '1 / 3' }}>
        <div style={s.cardHeader}>
          <span style={s.cardTitle}>MTD Actual vs Target vs Last Year</span>
          <button style={s.moreBtn}><MoreHorizontal size={16} /></button>
        </div>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={mtdChart} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f5" />
            <XAxis dataKey="year" tick={{ fontSize: 'clamp(8px, 0.7vw, 9px)', fill: '#6b7a8d' }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="left"  tickFormatter={fmtNum} tick={{ fontSize: 'clamp(8px, 0.7vw, 9px)', fill: '#6b7a8d' }} axisLine={false} tickLine={false} width={85} />
            {/* <YAxis yAxisId="right" orientation="right" tickFormatter={fmtNum} tick={{ fontSize: 11, fill: '#6b7a8d' }} axisLine={false} tickLine={false} width={85} /> */}
            <Tooltip contentStyle={tooltipStyle} formatter={fmtNum} />
            <Legend wrapperStyle={{ fontSize: 'clamp(8px, 0.7vw, 9px)', paddingTop: 8 }} />
            <Bar yAxisId="left"  dataKey="last"   name="Last"   fill="#c53030" radius={[4,4,0,0]} maxBarSize={32} isAnimationActive={!loading} />
            <Bar yAxisId="left"  dataKey="target" name="Target" fill="#59a14f" radius={[4,4,0,0]} maxBarSize={32} isAnimationActive={!loading} />
            <Line yAxisId="right" type="monotone" dataKey="actual" name="Actual" stroke="#f28e2b" strokeWidth={2.5} dot={{ r: 3, fill: '#f28e2b' }} isAnimationActive={!loading} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <PieCard title="Sales Contribution By Channel" data={channelPie} />
      <TopListCard title="List of TOP 10 Products"   data={top10Products} />
      <PieCard title="Sales Contribution By DIVISI"  data={divisiPie} />
      <TopListCard title="List of TOP 10 Outlets"    data={top10Outlets} />
    </div>
  );
}
